import { TrainingZoneLabel } from '../TrainingZoneLegend';
import { trainingZoneNumber, trainingZoneColor, trainingZoneCustomName, UNCLASSIFIED_ZONE_COLOR } from '../../utils/trainingZones';
import { useTranslation } from 'react-i18next';
import { workoutDisplayDigits as displayDigits, formatWorkoutNumber } from '../../utils/workoutNumberFormat';
import { formatZoneTime } from '../../utils/secToHMS';
import { ChartUnitValue } from './ChartUnitValue';
import type { AthleteZoneTime } from '../../utils/athleteZoneDistribution';
import '../../styles/analysis-zone-bars.css';

export function AthleteZoneBars({ zones, unit }: {
  zones: AthleteZoneTime[]; grads?: [string, string][]; unit: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const number = (value: number) => formatWorkoutNumber(value, locale, { maximumFractionDigits: 1 });
  const unitLabel = unit === 'W' ? t('workoutCurve.watts') : unit === 'bpm' ? t('athleteSettings.trainingEditor.bpm') : unit;
  if (!zones.length) return null;
  return <div className="athlete-zone-bars">
    {zones.some(zone => zone.invalidConfiguration) && <p className="analysis-zones-note">{t('analysis.zonesUnavailable')}</p>}
    <div className="athlete-zone-bars__list" role="list">{zones.map((zone, i) => {
      const zoneNumber = zone.zoneNumber ?? trainingZoneNumber(zone.label) ?? i + 1;
      const color = zone.unclassified ? UNCLASSIFIED_ZONE_COLOR : trainingZoneColor(zoneNumber);
      const seconds = Math.round(zone.time_in_seconds);
      const customName = trainingZoneCustomName(zone.label);
      const range = `${number(zone.minimum)}${zone.maximum == null ? '+' : `–${number(zone.maximum)}`}`;
      return <div className="athlete-zone-bars__row" role="listitem" data-zone-number={zone.unclassified ? undefined : zoneNumber} key={`${zoneNumber}-${i}`}>
        <div className="athlete-zone-bars__heading">
          <span className="athlete-zone-bars__name">{zone.unclassified
            ? <span className="athlete-zone-bars__outside"><span className="training-zone-legend__swatch" aria-hidden="true" style={{ backgroundColor: color }} />{t('analysis.zonesOutside')}</span>
            : <TrainingZoneLabel number={zoneNumber} name={customName ? displayDigits(customName, locale) : undefined} standardName={false} />}</span>
          <bdi className="athlete-zone-bars__percent">{zone.invalidConfiguration ? '—' : formatWorkoutNumber(zone.percent_in_zone / 100, locale, { style: 'percent', maximumFractionDigits: 1 })}</bdi>
        </div>
        <div className="athlete-zone-bars__track" aria-hidden="true"><div style={{ width: `${zone.invalidConfiguration ? 0 : Math.max(0, Math.min(100, zone.percent_in_zone))}%`, background: color }} /></div>
        <div className="athlete-zone-bars__meta">
          <span className="athlete-zone-bars__range">{!zone.unclassified && <ChartUnitValue value={range} unit={unitLabel} />}</span>
          <time dateTime={zone.invalidConfiguration ? undefined : `PT${seconds}S`} dir="ltr">{zone.invalidConfiguration ? '—' : displayDigits(formatZoneTime(seconds), locale)}</time>
        </div>
      </div>;
    })}</div>
  </div>;
}
